/**
 * useDealListKpis - Hook para buscar KPIs totalizadores da listagem de negócios
 * 
 * Consome a RPC `get_deal_list_kpis` que retorna contagens por status
 * respeitando permissões granulares (admin/supervisor/seller).
 * 
 * @version 1.0.0
 * RELEASE: 2026-04-09 v1.0.0 - Criação inicial
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import type { DealFilters } from '@/hooks/useCrmDeals';

/** Logger do módulo */
const logger = createLogger('useDealListKpis');

/** Estrutura dos KPIs retornados pela RPC */
export interface DealListKpis {
  total_deals: number;
  created_today: number;
  open: number;
  won: number;
  lost: number;
}

/** Valores padrão (zerados) */
const EMPTY_KPIS: DealListKpis = {
  total_deals: 0,
  created_today: 0,
  open: 0,
  won: 0,
  lost: 0,
};

/**
 * Hook para buscar KPIs totalizadores da listagem de negócios
 * Retorna contagens por status com permissões e filtros sincronizados
 * 
 * @param filters - Filtros ativos da listagem (sincroniza KPIs com tabela)
 */
export function useDealListKpis(filters?: DealFilters) {
  const { currentTenant } = useAuth();

  // v1.3.0 — Envia timezone do navegador para o KPI "Criados Hoje" respeitar o fuso local
  const browserTimezone =
    Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Sao_Paulo';

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['deal-list-kpis', currentTenant?.id, filters, browserTimezone],
    queryFn: async () => {
      logger.debug('Buscando KPIs da listagem de negócios', { filters, browserTimezone });

      const { data, error } = await supabase.rpc('get_deal_list_kpis', {
        p_search: filters?.search || null,
        p_workspace_id: filters?.workspace_id || null,
        p_pipeline_id: filters?.pipeline_id || null,
        p_status: filters?.status || null,
        p_priority: filters?.priority || null,
        p_seller_id: filters?.seller_id || null,
        p_assigned_to: filters?.assigned_to || null,
        p_source: filters?.source || null,
        p_probability_min: filters?.probability_min ?? null,
        p_probability_max: filters?.probability_max ?? null,
        p_created_from: filters?.created_from || null,
        p_created_to: filters?.created_to || null,
        p_won_date_from: filters?.won_date_from || null,
        p_won_date_to: filters?.won_date_to || null,
        p_lost_date_from: filters?.lost_date_from || null,
        p_lost_date_to: filters?.lost_date_to || null,
        p_phase_updated_from: filters?.phase_updated_from || null,
        p_phase_updated_to: filters?.phase_updated_to || null,
        p_tenant_hint: currentTenant?.id || null,
        // v1.1.0 (2026-04-23): Tags, Data Previsão e Fase
        // Mantém os KPIs do modal coerentes com o recorte especial de negócios sem tag.
        p_tag_ids: filters?.tags_is_empty
          ? ['00000000-0000-0000-0000-000000000000']
          : filters?.tag_ids && filters.tag_ids.length > 0 ? filters.tag_ids : null,
        p_expected_close_from: filters?.expected_close_from || null,
        p_expected_close_to: filters?.expected_close_to || null,
        p_phase_id: filters?.phase_id || null,
        // v1.2.0 (2026-05-16): Sincroniza KPIs com filtro de Origem do Cadastro
        p_creation_origin_types:
          filters?.creation_origin_types && filters.creation_origin_types.length > 0
            ? filters.creation_origin_types
            : null,
        p_creation_origin_source_id: filters?.creation_origin_source_id || null,
        // v1.3.0: timezone do navegador (default America/Sao_Paulo no servidor)
        p_timezone: browserTimezone,
      } as any);

      if (error) {
        logger.error('Erro ao buscar KPIs de negócios', error);
        throw error;
      }

      // RPC retorna array com 1 linha
      const row = Array.isArray(data) ? data[0] : data;
      if (!row) return EMPTY_KPIS;

      return {
        total_deals: Number(row.total_deals) || 0,
        created_today: Number(row.created_today) || 0,
        open: Number(row.open) || 0,
        won: Number(row.won) || 0,
        lost: Number(row.lost) || 0,
      } as DealListKpis;
    },
    enabled: !!currentTenant?.id,
    staleTime: 15_000,
    gcTime: 2 * 60_000,
  });

  return {
    kpis: data || EMPTY_KPIS,
    isLoading,
    refetch,
  };
}
